import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faPlus, faEye } from '@fortawesome/free-solid-svg-icons';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import formaEntregaService from '../../services/formaEntregaService';
import ColumnVisibilityModal from '../../components/ColumnVisibilityModal';
import Toast from '../../components/Toast';

const pageSizes = [5, 10, 20];

const FormaEntregaList = () => {
  const [filters, setFilters] = useState({ nome: '', status: '' });
  const [formas, setFormas] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizes[0]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pagination, setPagination] = useState({});
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const columns = [
    { Header: 'ID', accessor: 'idFormaEntrega' },
    { Header: 'Nome', accessor: 'nome' },
    { Header: 'Status', accessor: 'status' },
    { Header: 'Cadastrado Por', accessor: 'cadastradoPor' },
    { Header: 'Alterado Por', accessor: 'alteradoPor' },
    { Header: 'Cadastrado Em', accessor: 'cadastradoEm', Cell: value => {
      if (!value) return '';
      const date = new Date(value);
      const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
      return adjustedDate.toLocaleDateString('pt-BR');
    }},
    { Header: 'Alterado Em', accessor: 'alteradoEm', Cell: value => {
      if (!value) return '';
      const date = new Date(value);
      const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
      return adjustedDate.toLocaleDateString('pt-BR');
    }}
  ];

  const [visibleColumns, setVisibleColumns] = useState(columns.map(col => col.accessor));

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const loadFormas = useCallback(async () => {
    try {
      const params = {
        page,
        limit: pageSize,
        ...filters
      };
      const response = await formaEntregaService.list(params);
      setFormas(response.data || []);
      setPagination(response.pagination || {});
    } catch (error) {
      console.error('Erro ao carregar formas de entrega:', error);
      showToast('Erro ao carregar formas de entrega', 'error');
    }
  }, [page, pageSize, filters]);

  useEffect(() => {
    loadFormas();
  }, [loadFormas]);

  // Modal handlers
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handleToggleColumn = (accessor) => {
    setVisibleColumns(prev =>
      prev.includes(accessor)
        ? prev.filter(col => col !== accessor)
        : [...prev, accessor]
    );
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setPage(1);
  };

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden">
      <div className="px-2 sm:px-6">
        <div className="max-w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Listagem de Formas de Entrega</h2>
            <Link
              to="/forma-entrega/create"
              className="px-4 py-2 rounded bg-green-600 text-white font-semibold shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Nova Forma</span>
            </Link>
          </div>
          <div className="max-w-full">
            {/* Filtros */}
            <div className="flex flex-col sm:flex-row gap-2 mb-6">
              <input
                type="text"
                name="nome"
                value={filters.nome}
                onChange={handleFilterChange}
                placeholder="Filtrar por nome"
                className="border rounded px-2 py-1 w-full sm:w-48"
              />
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="border rounded px-2 py-1 w-full sm:w-40"
              >
                <option value="">Todos Status</option>
                <option value="Ativo">Ativo</option>
                <option value="Inativo">Inativo</option>
              </select>
            </div>
            {/* Controles */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2 max-w-full">
              <div className="order-2 sm:order-1 w-full sm:w-auto">
                {pagination && (
                  <Pagination 
                    currentPage={pagination.page || 1}
                    totalPages={pagination.pages || 1}
                    onPageChange={setPage}
                  />
                )}
              </div>
              <div className="order-1 sm:order-2 flex items-center w-full sm:w-auto justify-end gap-2">
                <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap mr-2">Por página:</label>
                <select
                  id="pageSize"
                  value={pageSize}
                  onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
                >
                  {pageSizes.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="flex items-center gap-2 px-3 py-1 rounded bg-green-600 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
                  onClick={handleOpenModal}
                  aria-label="Exibir/Ocultar colunas"
                >
                  <FontAwesomeIcon icon={faEye} />
                  <span>Colunas</span>
                </button>
              </div>
            </div>
            <ColumnVisibilityModal
              columns={columns}
              visibleColumns={visibleColumns}
              onChange={handleToggleColumn}
              onClose={handleCloseModal}
              isOpen={isModalOpen}
            />
            <Table
              columns={columns.filter(col => visibleColumns.includes(col.accessor)).map(col => {
                if (col.accessor === 'nome') {
                  return {
                    ...col,
                    Cell: (value, row) => (
                      <div className="flex items-center gap-2">
                        <span>{value}</span>
                        <Link
                          to={`/forma-entrega/${row.idFormaEntrega}/edit`}
                          className="text-green-600 hover:text-green-800 p-1 rounded focus:outline-none"
                          title="Editar"
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </Link>
                      </div>
                    )
                  };
                }
                return col;
              })}
              data={formas}
            />
          </div>
        </div>
      </div>
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
};

export default FormaEntregaList;
